import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { isValidPolygon, polygonInsidePolygon, unionAreaKmSq, type GeoPolygon } from '@soc/shared';
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetMyOrganizationQuery,
  useGetOrganizationsQuery,
  useGetProjectsQuery,
  useGetUsersQuery,
  useUpdateProjectMutation,
} from '../app/api';
import { useAuth } from '../features/auth/useAuth';
import { apiErrorMessage } from '../lib/validation';
import { ErrorNote, Field, inputStyle, primaryButtonStyle } from '../features/admin/adminUi';
import {
  AdminFormShell,
  FormHeader,
  FormSection,
  FormSplit,
  RecordMeta,
  SegmentedControl,
  dangerBtnStyle,
  secondaryBtnStyle,
} from '../features/admin/formUi';
import { PolyMap } from '../features/admin/PolyMap';
import { QuotaBar } from '../features/admin/QuotaBar';

// Cosmetic-only reference fields (rendered for fidelity, not persisted).
const LAYERS = [
  { value: 'lte', label: 'LTE' },
  { value: '5gnr', label: '5G NR' },
  { value: '2g', label: '2G' },
  { value: '3g', label: '3G' },
];
const RULESETS = [
  { value: 'standard', label: 'Standard anomaly set' },
  { value: 'strict', label: 'Strict' },
  { value: 'custom', label: 'Custom' },
];
const CHANNELS = [
  { value: 'inapp', label: 'In-app feed' },
  { value: 'email', label: 'Email' },
  { value: 'webhook', label: 'Webhook' },
];

export default function ProjectForm({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';

  const { data: projects } = useGetProjectsQuery();
  const { data: orgs } = useGetOrganizationsQuery(undefined, { skip: !isSuper });
  const { data: myOrg } = useGetMyOrganizationQuery(undefined, { skip: isSuper });
  const { data: users } = useGetUsersQuery();

  const [createProject, { isLoading: creating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: updating }] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();

  const record = mode === 'edit' ? projects?.items.find((p) => p.id === id) : undefined;

  // real, persisted fields
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [polygon, setPolygon] = useState<GeoPolygon | null>(null);
  // cosmetic
  const [lead, setLead] = useState('');
  const [layers, setLayers] = useState<string[]>(['lte', '5gnr']);
  const [ruleset, setRuleset] = useState('standard');
  const [channel, setChannel] = useState('inapp');
  const [description, setDescription] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(mode === 'create');

  useEffect(() => {
    if (mode === 'edit' && record && !hydrated) {
      setName(record.name);
      setOrgId(record.orgId ?? '');
      setPolygon(record.polygon);
      setHydrated(true);
    }
  }, [mode, record, hydrated]);

  // The effective org (with coverage boundary + budget) for the chosen/owning org.
  const selectedOrg = isSuper ? orgs?.items.find((o) => o.id === orgId) : myOrg ?? undefined;
  const boundary = selectedOrg?.polygon ?? null;
  const maxArea = selectedOrg?.maxAreaKmSq ?? null;
  const selectedOrgId = isSuper ? orgId || null : myOrg?.id ?? null;

  // Existing project polygons in the org (exclude the one being edited).
  const others = useMemo(() => {
    const items = projects?.items ?? [];
    return items
      .filter((p) => p.id !== id)
      .filter((p) => (isSuper ? p.orgId === selectedOrgId : true))
      .map((p) => p.polygon);
  }, [projects, id, isSuper, selectedOrgId]);

  // Coverage is the UNION of polygons (overlaps counted once), mirroring the
  // backend budget enforcement so the pre-check and the server agree.
  const usedKmSq = useMemo(() => unionAreaKmSq(others), [others]);
  const pendingKmSq = useMemo(
    () => (polygon ? Math.max(0, unionAreaKmSq([...others, polygon]) - usedKmSq) : 0),
    [others, polygon, usedKmSq],
  );

  const selfIntersecting = !!polygon && !isValidPolygon(polygon);
  const outOfBounds = !!(polygon && boundary && !polygonInsidePolygon(polygon, boundary));
  const overBudget = maxArea != null && usedKmSq + pendingKmSq > maxArea;
  const invalid = !!polygon && (selfIntersecting || outOfBounds || overBudget);
  const invalidMessage = selfIntersecting
    ? 'Self-intersecting area'
    : outOfBounds
      ? 'Outside org boundary'
      : overBudget
        ? 'Over coverage budget'
        : undefined;

  const orgChosen = isSuper ? orgId.length > 0 : true;
  const saving = creating || updating;
  const canSave = name.trim().length > 0 && polygon != null && orgChosen && !invalid && !saving;

  const submit = async () => {
    setError(null);
    if (!canSave || !polygon) return;
    try {
      if (mode === 'create') {
        await createProject({ name: name.trim(), polygon, ...(isSuper ? { orgId } : {}) }).unwrap();
      } else if (id) {
        await updateProject({ id, name: name.trim(), polygon }).unwrap();
      }
      navigate('/projects');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save project'));
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm('Delete this project?')) return;
    try {
      await deleteProject(id).unwrap();
      navigate('/projects');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to delete project'));
    }
  };

  const toggleLayer = (v: string) =>
    setLayers((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const actions = (
    <>
      {mode === 'edit' && (
        <button type="button" style={dangerBtnStyle} onClick={remove}>
          Delete
        </button>
      )}
      <Link to="/projects" style={secondaryBtnStyle}>
        Cancel
      </Link>
      <button type="button" style={primaryButtonStyle(canSave)} disabled={!canSave} onClick={submit}>
        {saving ? 'Saving…' : mode === 'create' ? 'Create project' : 'Save changes'}
      </button>
    </>
  );

  const ready = mode === 'create' || hydrated;

  return (
    <AdminFormShell activeEntity="projects" actions={actions}>
      <FormSplit
        form={
          <>
            <FormHeader
              eyebrow={mode === 'create' ? 'New project' : 'Edit project'}
              title={mode === 'create' ? 'Add a project' : record?.name ?? 'Project'}
              subtitle="A project scopes a set of towers, detection rules and a monitored area under an organization."
              meta={mode === 'edit' && record ? <RecordMeta id={record.id} createdAt={record.createdAt} /> : undefined}
            />

            <FormSection title="Details">
              <Field label="Project name">
                <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Metro Core Watch" />
              </Field>
              <Field label="Parent organization">
                {isSuper ? (
                  <select
                    style={inputStyle}
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    disabled={mode === 'edit'}
                  >
                    <option value="">— select —</option>
                    {orgs?.items.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input style={{ ...inputStyle, opacity: 0.7 }} value={myOrg?.name ?? '—'} disabled />
                )}
              </Field>
              <Field label="Project lead">
                <select style={inputStyle} value={lead} onChange={(e) => setLead(e.target.value)}>
                  <option value="">— unassigned —</option>
                  {users?.items.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </Field>
            </FormSection>

            <FormSection title="Network layers">
              <SegmentedControl options={LAYERS} isActive={(v) => layers.includes(v)} onClick={toggleLayer} />
            </FormSection>

            <FormSection title="Configuration">
              <Field label="Detection ruleset">
                <select style={inputStyle} value={ruleset} onChange={(e) => setRuleset(e.target.value)}>
                  {RULESETS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Alert channel">
                <select style={inputStyle} value={channel} onChange={(e) => setChannel(e.target.value)}>
                  {CHANNELS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Description">
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Continuous anomaly watch over the downtown high-density cluster."
                />
              </Field>
            </FormSection>

            <FormSection title="Monitored area">
              <QuotaBar usedKmSq={usedKmSq} pendingKmSq={pendingKmSq} maxKmSq={maxArea} />
            </FormSection>

            {error && <ErrorNote>{error}</ErrorNote>}
          </>
        }
        panel={
          ready ? (
            <PolyMap
              initialPolygon={mode === 'edit' ? record?.polygon ?? null : null}
              onChange={setPolygon}
              boundary={boundary}
              invalid={invalid}
              invalidMessage={invalidMessage}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--text-faint)' }}>
              Loading…
            </div>
          )
        }
      />
    </AdminFormShell>
  );
}
